import { useCallback, useMemo } from 'react';

import { useCardApiContext } from '@/microfrontends/listing/shared/context/CardApiContext';
import { useCardContext } from '@/microfrontends/listing/shared/context/CardContext';
import { useListingAppContext } from '@/microfrontends/listing/shared/context/ListingAppContext';
import { cardOfferDataSelector, cardOfferSelector } from '@/microfrontends/listing/shared/context/selectors/cardData';

import { useRequestAuthentication } from '@/packages/auth/useRequestAuthentication';
import { useAddFavoriteMutation } from '@/packages/query-state/shared/store/favorites/v1/add-favorite/mutation';
import { useDeleteOfferFromFavoritesMutation } from '@/packages/query-state/shared/store/favorites/v1/delete-offer-from-favorites/mutation';
import { useUserIsAuthenticatedSelector } from '@/packages/redux-state/store/slices/user';

import { useCAForCard } from './useCAForCard';

export function useToggleFavorite(): VoidFunction {
  const cardContext = useCardContext();
  const appContext = useListingAppContext();
  const cardApi = useCardApiContext();
  const { dealType, offer } = cardContext;
  const {
    extendedProperties: { isFavorite },
  } = cardOfferDataSelector(cardContext);
  const { id: offerId } = cardOfferSelector(cardContext);
  const isUserAuthenticated = useUserIsAuthenticatedSelector();
  const requestAuthentication = useRequestAuthentication(appContext);

  const { mutate: addToFavorite } = useAddFavoriteMutation(cardApi.addToFavoriteMutationOptions);
  const { mutate: deleteFromFavorite } = useDeleteOfferFromFavoritesMutation(cardApi.deleteFromFavoriteMutationOptions);

  /**
   * @todo подумать над лучшим способом определения типа объекта недвижимости
   */
  const entityTypeFromUrl = useMemo(() => {
    const matches = offer.data.url.match(/(suburban|commercial|flat)/);

    if (matches?.[0] === 'commercial') {
      return 'offerCommercial';
    }
    if (matches?.[0] === 'suburban') {
      return 'offerSuburban';
    }

    return 'offerResidental';
  }, [offer.data.url]);

  const caForCard = useCAForCard();

  return useCallback<VoidFunction>(() => {
    if (isUserAuthenticated) {
      caForCard(
        { action: isFavorite ? 'remove_from_newlist' : 'add_from_newlist', category: 'favorite' },
        { label: true },
      );

      if (isFavorite) {
        deleteFromFavorite({ dealType, entityId: offerId, entityType: entityTypeFromUrl });
      } else {
        addToFavorite({ dealType, entityId: offerId, entityType: entityTypeFromUrl });
      }
    } else {
      requestAuthentication();
    }
  }, [
    addToFavorite,
    caForCard,
    dealType,
    deleteFromFavorite,
    isFavorite,
    isUserAuthenticated,
    offerId,
    requestAuthentication,
    entityTypeFromUrl,
  ]);
}
